import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { stylePropType } from 'utils/propTypes';
import { adjustColorAlpha, convertRGBtoRGBA } from 'utils/colorConvert';
import withBadge from 'hoc/withBadge';
import withTooltip from 'hoc/withTooltip';

// 參考分享按鈕、訂閱通知按鈕
/**
 * @method FeverIconButton - 包含 IconButton 的按鈕
 * @param {Object}  props
 * @param {String}  props.variant          - 按鈕類型 text | outlined | contained
 * @param {String}  props.href             - 點擊按鈕時連結到的 URL。如果定義，a 元素將用作根節點
 * @param {String}  props.imageUrl         - 按鈕 icon 的圖片連結
 * @param {String}  props.text             - 按鈕 icon 隔壁的文字
 * @param {String}  props.textColor        - 按鈕 icon 隔壁的文字顏色
 * @param {String}  props.bgColor          - 按鈕主題顏色 (背景顏色)
 * @param {React.ReactNode}  props.icon    - 按鈕 icon 元件 (必須是 Component)
 * @param {String}  props.iconVariant      - icon 按鈕類型 default | outlined | contained
 * @param {String}  props.iconPosition     - icon 位置 start | end
 * @param {Number}  props.iconSize         - icon 按鈕大小
 * @param {Object}  props.iconStyle        - icon 本身 style
 * @param {Object}  props.iconButtonStyle  - icon 按鈕 style
 * @param {Object}  props.buttonStyle      - 客製化按鈕 style
 * @param {Boolean} props.loading          - 讀取中
 * @param {String}  props.loadingPosition  - loading icon 位置 start | center | end
 * @param {String}  props.loadingIndicator - 克制讀取中的文字 (取代 CircularProgress 顯示文字)
 * @param {Boolean} props.disabled         - 禁用
 * @param {Function}props.onClick          - 點擊事件
 * @returns {JSX.Element}
 */

export default function FeverIconButton({
    variant = 'contained',
    href,
    imageUrl,
    text,
    textColor,
    iconColor,
    bgColor,
    icon: IconComp,
    iconVariant = 'default',
    iconPosition = 'start',
    iconSize = 28,
    iconStyle = {},
    iconButtonStyle = {},
    buttonStyle = {},
    loading = false,
    loadingPosition,
    loadingIndicator = '',
    disabled = false,
    onClick = () => {},
    ...props
}) {
    const renderIcon = () => {
        let iconContent = null;
        if (loading) {
            return (
                <div css={[circularProgressStyle({ iconSize, bgColor }), iconStyle]}>
                    <CircularProgress style={{ width: '100%', height: '100%' }} />
                </div>
            );
        }

        if (IconComp) {
            iconContent = <IconComp css={[iconBaseStyle({ iconSize }), iconStyle]} />;
        }

        if (imageUrl) {
            iconContent = (
                <img src={imageUrl} alt={imageUrl} css={[iconImgStyle({ iconSize }), iconStyle]} />
            );
        }

        return iconContent;
    };

    const getIcon = (iconPosition, loading, loadingPosition) => {
        if (loading && loadingPosition !== 'center' && iconPosition === loadingPosition) {
            return (
                <div css={[circularProgressStyle({ iconSize, textColor }), iconStyle]}>
                    <CircularProgress style={{ width: '100%', height: '100%' }} />
                </div>
            );
        }
        // TODO: fix Warning validateDOMNesting(...): <button> cannot appear as a descendant of <button>.
        return (
            <IconButton
                css={[
                    iconButtonBaseStyle({ iconVariant, textColor, bgColor, text }),
                    iconButtonStyle
                ]}
                disabled={disabled}
                disableFocusRipple={disabled}
                disableRipple={disabled}
            >
                {renderIcon()}
            </IconButton>
        );
    };

    const getButtonText = (loading, loadingPosition, text) => {
        if (loading && loadingPosition === 'center') {
            return loadingIndicator ? (
                loadingIndicator
            ) : (
                <div css={[circularProgressStyle({ iconSize, textColor }), iconStyle]}>
                    <CircularProgress style={{ width: '100%', height: '100%' }} />
                </div>
            );
        }
        return text;
    };

    const renderButton = () => {
        if (text !== undefined) {
            return (
                <Button
                    css={[
                        buttonBaseStyle({ variant, textColor, bgColor, text, iconSize }),
                        buttonStyle
                    ]}
                    variant={variant}
                    disabled={disabled}
                    href={href}
                    onClick={onClick}
                    {...props}
                >
                    {iconPosition === 'start' && loadingPosition !== 'center' && (
                        <Box display="flex" mr={text === '' ? 0 : 1}>
                            {getIcon(iconPosition, loading, loadingPosition)}
                        </Box>
                    )}
                    <Box display="flex" css={buttonTextStyle}>
                        {getButtonText(loading, loadingPosition, text)}
                    </Box>
                    {iconPosition === 'end' && loadingPosition !== 'center' && (
                        <Box display="flex" ml={text === '' ? 0 : 1}>
                            {getIcon(iconPosition, loading, loadingPosition)}
                        </Box>
                    )}
                </Button>
            );
        }

        return (
            <IconButton
                css={[
                    iconButtonBaseStyle({ iconVariant, textColor, bgColor, text }),
                    iconButtonStyle
                ]}
                disabled={disabled}
                disableFocusRipple={disabled}
                disableRipple={disabled}
                onClick={onClick}
                {...props}
            >
                {renderIcon()}
            </IconButton>
        );
    };
    return renderButton();
}

const iconButtonBaseStyle = ({ iconVariant, textColor, bgColor, text, disableHover = true }) => css`
    box-shadow: none;
    ${
        iconVariant === 'default' &&
        `
        background-color: transparent;
        border: none;
        color: ${text !== undefined ? textColor : 'inherit'};
    `
    }
    ${
        iconVariant === 'outlined' &&
        `
        background-color: transparent;
        border: 1px solid;
        color: ${text !== undefined ? bgColor : textColor || bgColor};
        border-color: ${text !== undefined ? bgColor : textColor || bgColor};

        &:hover {
            background-color: ${adjustColorAlpha(bgColor, 0.1)}
        }
    `
    }
    ${
        iconVariant === 'contained' &&
        `
        background-color: ${text !== undefined ? textColor : bgColor};
        border: none;
        color: ${text !== undefined ? bgColor : 'inherit'};

        &:hover {
            background-color: ${
                disableHover
                    ? text !== undefined
                        ? textColor
                        : bgColor
                    : convertRGBtoRGBA(bgColor, 0.3)
            }
        }
    `
    }
    
`;

const iconBaseStyle = ({ iconSize }) => css`
    font-size: ${iconSize}px;
`;

const iconImgStyle = ({ iconSize }) => css`
    width: ${iconSize}px;
    height: ${iconSize}px;
`;

const buttonBaseStyle = ({ variant, textColor, bgColor, text = '', iconSize }) => css`
    display: flex;
    align-items: center;
    padding-block: 14px;
    min-width: ${text === '' && iconSize + 8 * 2 + 4 * 2}px;
    box-shadow: none;
  
    & .MuiButton-label {
        ${
            text === '' &&
            `aspect-ratio: 1;  width: ${iconSize + 8 * 2}px; height: ${iconSize + 8 * 2}px;`
        }
    }
    ${
        variant === 'text' &&
        `
        color: ${textColor};
        background-color: transparent;
        border: none;
    `
    }
    ${
        variant === 'outlined' &&
        `
        color: ${bgColor};
        background-color: transparent;
        border: 1px solid;
        border-color: ${bgColor};
        border-radius: 50px;
    `
    }
    ${
        variant === 'contained' &&
        `
        color: ${textColor};
        background-color: ${bgColor};
        border-radius: 50px;

        &:hover {
            box-shadow: none;
            background-color: ${bgColor};
        }
    `
    }

`;

const circularProgressStyle = ({ iconSize, bgColor, textColor }) => css`
    width: ${iconSize}px;
    height: ${iconSize}px;
    svg {
        color: ${bgColor || textColor};
    }
`;

const buttonTextStyle = css`
    letter-spacing: 0.1px;
`;

FeverIconButton.propTypes = {
    variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
    href: PropTypes.string,
    imageUrl: PropTypes.string,
    text: PropTypes.string,
    textColor: PropTypes.string,
    bgColor: PropTypes.string,
    icon: PropTypes.object,
    iconVariant: PropTypes.oneOf(['default', 'outlined', 'contained']),
    iconPosition: PropTypes.oneOf(['start', 'center', 'end']),
    iconSize: PropTypes.number,
    iconStyle: stylePropType,
    iconButtonStyle: stylePropType,
    buttonStyle: stylePropType,
    loading: PropTypes.bool,
    loadingPosition: PropTypes.oneOf(['start', 'center', 'end']),
    loadingIndicator: PropTypes.string,
    disabled: PropTypes.bool,
    onClick: PropTypes.func
};

export const BadgeIconButton = withBadge(FeverIconButton);

export const TooltipIconButton = withTooltip(FeverIconButton);
